import { assertNever } from "@redotech/util/type";
import { ProductTotals, ReturnTotals } from "../../return-totals-calculator";
import {
  SerializedProductSummaryTotals,
  SerializedReturnSummaryTotals,
} from "./types";

export const RAC_LOG_STAMP = "RAC DARK LAUNCH";

export enum ReturnTotalsType {
  LEGACY = "legacy",
  NEW = "new",
}

type NumberKeys<T> = {
  [K in keyof T]: T[K] extends number ? K : never;
}[keyof T];

const fieldsMatch = (legacyField: number, newField: number): boolean => {
  // The old RTC has a lot of rounding issues, so ignore differences less than 0.001
  return Math.abs(legacyField - newField) < 0.001;
};

// ------------ Return Totals Set ------------ //

// Allows us to dark launch and hide return accounting calculator behind a feature flag
export type ReturnTotalsSet = {
  legacy: ReturnTotals;
  new: SerializedReturnSummaryTotals | undefined;
  typeToUse: ReturnTotalsType;
};

const getSingleFieldFromReturnTotalsSet = ({
  returnTotalsSet,
  legacyFieldName,
  newFieldName,
}: {
  returnTotalsSet: ReturnTotalsSet;
  legacyFieldName: NumberKeys<ReturnTotals>;
  // TODO(devin): Once all fields are added to the new return totals, we can remove the null check.
  newFieldName: NumberKeys<SerializedReturnSummaryTotals> | null;
}): number => {
  const legacyField = returnTotalsSet.legacy[legacyFieldName];
  if (!returnTotalsSet.new || !newFieldName) {
    return legacyField;
  }
  const newField = returnTotalsSet.new[newFieldName];
  if (!fieldsMatch(legacyField, newField)) {
    console.error(
      `${RAC_LOG_STAMP} New field ${newFieldName} (${newField}) does not match legacy field ${legacyFieldName} (${legacyField})`,
    );
  }
  switch (returnTotalsSet.typeToUse) {
    case ReturnTotalsType.LEGACY:
      return legacyField;
    case ReturnTotalsType.NEW:
      return newField;
    default:
      assertNever(returnTotalsSet.typeToUse);
  }
};

type ReturnTotalsField<T extends NumberKeys<ReturnTotals>> = {
  legacyFieldName: T;
  // TODO(devin): Once all fields are added to the new return totals, we can remove the null check.
  newFieldName: NumberKeys<SerializedReturnSummaryTotals> | null;
};

export const getFieldsFromReturnTotalsSet = <
  T extends NumberKeys<ReturnTotals>,
>({
  returnTotalsSet,
  fields,
}: {
  returnTotalsSet: ReturnTotalsSet;
  fields: ReturnTotalsField<T>[];
}): Record<T, number> => {
  return fields.reduce(
    (acc, field) => {
      acc[field.legacyFieldName] = getSingleFieldFromReturnTotalsSet({
        returnTotalsSet,
        legacyFieldName: field.legacyFieldName,
        newFieldName: field.newFieldName,
      });
      return acc;
    },
    {} as Record<T, number>,
  );
};

// ------------ Product Totals Set ------------ //

export type ProductTotalsSet = {
  legacy: ProductTotals;
  new: SerializedProductSummaryTotals | undefined;
  typeToUse: ReturnTotalsType;
};

const getSingleFieldFromProductTotalsSet = ({
  productTotalsSet,
  legacyFieldName,
  newFieldName,
}: {
  productTotalsSet: ProductTotalsSet;
  legacyFieldName: NumberKeys<ProductTotals>;
  // TODO(devin): Once all fields are added to the new return totals, we can remove the null check.
  newFieldName: NumberKeys<SerializedProductSummaryTotals> | null;
}): number => {
  const legacyField = productTotalsSet.legacy[legacyFieldName];
  if (!productTotalsSet.new || !newFieldName) {
    return legacyField;
  }
  const newField = productTotalsSet.new[newFieldName];
  if (!fieldsMatch(legacyField, newField)) {
    console.error(
      `${RAC_LOG_STAMP} New field ${newFieldName} (${newField}) does not match legacy field ${legacyFieldName} (${legacyField})`,
    );
  }
  switch (productTotalsSet.typeToUse) {
    case ReturnTotalsType.LEGACY:
      return legacyField;
    case ReturnTotalsType.NEW:
      return newField;
    default:
      assertNever(productTotalsSet.typeToUse);
  }
};

type ProductTotalsField<T extends NumberKeys<ProductTotals>> = {
  legacyFieldName: T;
  // TODO(devin): Once all fields are added to the new return totals, we can remove the null check.
  newFieldName: NumberKeys<SerializedProductSummaryTotals> | null;
};

export const getProductFieldsFromReturnTotalsSet = <
  T extends NumberKeys<ProductTotals>,
>({
  returnTotalsSet,
  productId,
  fields,
}: {
  returnTotalsSet: ReturnTotalsSet;
  productId: string;
  fields: ProductTotalsField<T>[];
}): Record<T, number> | undefined => {
  const legacyProductTotals = returnTotalsSet.legacy.productTotals.find(
    (p) => p.product._id?.toString() === productId,
  );
  if (!legacyProductTotals) {
    console.error(
      `${RAC_LOG_STAMP} Legacy product totals not found for product ${productId}`,
    );
    return undefined;
  }
  const newProductTotals = returnTotalsSet.new?.productTotalsList.find(
    (p) => p.id === productId,
  );
  if (!newProductTotals) {
    console.error(
      `${RAC_LOG_STAMP} New product totals not found for product ${productId}`,
    );
    if (returnTotalsSet.typeToUse === ReturnTotalsType.NEW) {
      throw new Error(`New product totals not found for product ${productId}`);
    }
  }
  const productTotalsSet = {
    legacy: legacyProductTotals,
    new: newProductTotals,
    typeToUse: returnTotalsSet.typeToUse,
  };
  return fields.reduce(
    (acc, field) => {
      acc[field.legacyFieldName] = getSingleFieldFromProductTotalsSet({
        productTotalsSet,
        legacyFieldName: field.legacyFieldName,
        newFieldName: field.newFieldName,
      });
      return acc;
    },
    {} as Record<T, number>,
  );
};
